// src/components/dashboard/RegistrationDashboard.jsx
import React, { useState, useEffect } from 'react';
import { Search, Edit, Trash, Download, Plus, Check, AlertCircle, FileText, X, Filter, RefreshCw, Package, ClipboardList } from 'lucide-react';
import RegistrationForm from './RegistrationForm';
import { formatDate } from '../../utils/date-formatter';
import { registrationService } from '../../services/registrationService';
import { useLanguage } from '../../contexts/LanguageContext';

const Notification = ({ message, type, onClose }) => {
  if (!message) return null;
  
  return (
    <div className={`fixed top-4 right-4 p-4 rounded-md shadow-md max-w-md z-50 flex items-center justify-between ${
      type === 'success' ? 'bg-green-100 text-green-800 border border-green-200' : 
      type === 'error' ? 'bg-red-100 text-red-800 border border-red-200' : 
      'bg-blue-100 text-blue-800 border border-blue-200'
    }`}>
      <div className="flex items-center">
        {type === 'success' && <Check className="h-5 w-5 mr-2" />}
        {type === 'error' && <AlertCircle className="h-5 w-5 mr-2" />}
        {type === 'info' && <FileText className="h-5 w-5 mr-2" />}
        <p>{message}</p>
      </div>
      <button onClick={onClose} className="ml-4 text-gray-500 hover:text-gray-700">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export const RegistrationDashboard = () => {
  const { t } = useLanguage();
  const [registrations, setRegistrations] = useState([]);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [notification, setNotification] = useState({ message: '', type: '' });
  
  const [filters, setFilters] = useState({
    number: '',
    phone: '',
    dateFrom: '',
    dateTo: '',
    entryPoint: '',
    exitPoint: '',
    status: '',
    transit_in: '',
    transit_out: ''
  });

  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);

  const [stats, setStats] = useState({
    total: 0,
    transit_in: 0,
    transit_out: 0,
    parking_entry: 0,
    parking_exit: 0,
    parking_40: false,
    remaining: {
      transit_card_remain: 0,
      parking_entry_remain: 0,
      parking_exit_remain: 0,
      parking_40_remain: 0
    }
  });

  useEffect(() => {
    fetchRegistrations();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification({ message: '', type: '' }), 5000);
  };

  const fetchRegistrations = async (currentFilters = filters) => {
    try {
      setIsLoading(true);
      const response = await registrationService.getList(currentFilters);
      setRegistrations(response.results);
      setStats(response.stats);
      setCurrentPage(1);
    } catch (error) {
      console.error('Failed to fetch registrations:', error);
      showNotification('Failed to fetch registrations', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleFormClose = () => {
    setIsFormOpen(false);
    setSelectedRegistration(null);
  };

  const handleRegistrationSubmit = async (formData) => {
    try {
      setIsLoading(true);
      if (selectedRegistration) {
        await registrationService.update(selectedRegistration.id, formData);
        showNotification(t('Registration_updated'), 'success');
      } else {
        await registrationService.create(formData);
        showNotification(t('Registration_created'), 'success');
      }
      await fetchRegistrations();
      handleFormClose();
    } catch (error) {
      console.error('Failed to save registration:', error);
      showNotification('Registration_failed', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditRegistration = async (id) => {
    try {
      const registration = registrations.find(reg => reg.id === id);
      if (registration) {
        setSelectedRegistration(registration);
        setIsFormOpen(true);
      }
    } catch (error) {
      console.error(`Failed to get registration ${id}:`, error);
      showNotification('Failed to get registration details', 'error');
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await fetchRegistrations(filters);
  };

  const handleFilterReset = async () => {
    const resetFilters = {
      number: '',
      phone: '',
      dateFrom: '',
      dateTo: '',
      entryPoint: '',
      exitPoint: '',
      status: '',
      transit_in: '',
      transit_out: ''
    };
    setFilters(resetFilters);
    await fetchRegistrations(resetFilters);
  };

  const handleDelete = async (id) => {
    if (window.confirm(t('confirmDelete'))) {
      try {
        setIsLoading(true);
        await registrationService.delete(id);
        showNotification(t('Registration deleted successfully'), 'success');
        await fetchRegistrations();
      } catch (error) {
        console.error('Failed to delete registration:', error);
        showNotification('Failed to delete registration', 'error');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'on_road': return 'bg-yellow-100 text-yellow-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };


  const handleExportToExcel = () => {
    if (registrations.length === 0) {
      showNotification('No registrations to export', 'info');
      return;
    }

    // Create HTML table for Excel
    let tableHTML = '<table border="1">';
    
    // Add headers
    tableHTML += '<thead><tr>';
    const headers = [
      'N', 'Registration Number', 'Trailer Number', 'Status', 'Transit In', 'Transit Out',
      'Departure Date', 'Entry Point', 'Terminal Entry Date', 'Terminal Exit Date',
      'Ticket', 'Parking 40', 'Exit Point'
    ];
    headers.forEach(header => {
      tableHTML += `<th>${header}</th>`;
    });
    tableHTML += '</tr></thead>';
    
    // Add data rows
    tableHTML += '<tbody>';
    registrations.forEach((registration, index) => {
      tableHTML += '<tr>';
      tableHTML += `<td>${index + 1}</td>`;
      tableHTML += `<td>${registration.number || '-'}</td>`;
      tableHTML += `<td>${registration.trailer_number || '-'}</td>`;
      tableHTML += `<td>${registration.status || '-'}</td>`;
      tableHTML += `<td>${registration.transit_in || 0}</td>`;
      tableHTML += `<td>${registration.transit_out || 0}</td>`;
      tableHTML += `<td>${formatDate(registration.departure_date)}</td>`;
      tableHTML += `<td>${registration.entry_point || '-'}</td>`;
      tableHTML += `<td>${formatDate(registration.terminal_entry_date)}</td>`;
      tableHTML += `<td>${formatDate(registration.terminal_exit_date)}</td>`;
      tableHTML += `<td>${registration.ticket || '-'}</td>`;
      tableHTML += `<td>${registration.parking_40 ? '✓' : '-'}</td>`;
      tableHTML += `<td>${registration.exit_point || '-'}</td>`;
      tableHTML += '</tr>';
    });
    tableHTML += '</tbody></table>';

    // Create blob with proper Excel format
    const blob = new Blob([tableHTML], { 
      type: 'application/vnd.ms-excel' 
    });
    
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    
    link.setAttribute('href', url);
    link.setAttribute('download', `registrations_${new Date().toISOString().split('T')[0]}.xls`);
    link.style.visibility = 'hidden';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up
    setTimeout(() => URL.revokeObjectURL(url), 100);
    
    showNotification('Registrations exported successfully', 'success');
  };

  return (
    <div className="container mx-auto px-2 sm:px-4 lg:px-6 py-4 sm:py-6">
      <Notification 
        message={notification.message} 
        type={notification.type} 
        onClose={() => setNotification({ message: '', type: '' })} 
      />
      
      {/* RESPONSIVE HEADER */}
      <div className="mb-4 sm:mb-6">
        <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-800">{t('registrationsremain')}</h1>
          
          <div className="flex flex-wrap gap-1 sm:gap-2">
            <button
              onClick={handleExportToExcel}
              className="px-2 sm:px-3 py-1.5 sm:py-2 bg-green-600 text-white rounded-md hover:bg-green-700 flex items-center text-xs sm:text-sm"
              disabled={isLoading || registrations.length === 0}
            >
              <Download className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('exportExcel')}</span>
              <span className="sm:hidden">Excel</span>
            </button>
            
            <button
              onClick={() => setIsFormOpen(true)}
              className="px-2 sm:px-3 py-1.5 sm:py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 flex items-center text-xs sm:text-sm"
            >
              <Plus className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('register')}</span>
              <span className="sm:hidden">Add</span>
            </button>
            
            <button
              onClick={() => setIsFilterExpanded(!isFilterExpanded)}
              className={`px-2 sm:px-3 py-1.5 sm:py-2 rounded-md flex items-center text-xs sm:text-sm ${
                isFilterExpanded 
                  ? 'bg-primary-100 text-primary-800' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="mr-1 h-3 w-3 sm:h-4 sm:w-4" />
              <span className="hidden sm:inline">{t('filters')}</span>
              <span className="sm:hidden">Filter</span>
            </button>
            
            <button
              onClick={() => fetchRegistrations()}
              className="px-2 sm:px-3 py-1.5 sm:py-2 bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100 flex items-center text-xs sm:text-sm"
              disabled={isLoading}
            >
              <RefreshCw className={`mr-1 h-3 w-3 sm:h-4 sm:w-4 ${isLoading ? 'animate-spin' : ''}`} />
              <span className="hidden sm:inline">{t('refresh')}</span>
              <span className="sm:hidden">↻</span>
            </button>
          </div>
        </div>
      </div>
      
      {/* RESPONSIVE STATS GRID */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2 mb-2">
        {/* Transit cards remaining */}
        <div className="bg-white p-3 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">{t('totalTransitRemain')}</p>
              <p className={`text-xl font-bold ${
              stats.remaining.transit_card_remain <= 0 ? 'text-red-600' :
              stats.remaining.transit_card_remain <= 5 ? 'text-yellow-600' :
              'text-green-600'
            }`}>{stats.remaining.transit_card_remain}</p>
            </div>
            <div className={`p-2 rounded-full ${
              stats.remaining.transit_card_remain <= 0 ? 'bg-red-100' :
              stats.remaining.transit_card_remain <= 5 ? 'bg-yellow-100' :
              'bg-green-100'
            }`}>
              <Package className={`h-4 w-4 ${
              stats.remaining.transit_card_remain <= 0 ? 'text-red-600' :
              stats.remaining.transit_card_remain <= 5 ? 'text-yellow-600' :
              'text-green-600'
            }`} />
            </div>
          </div>
        </div>

        {/* Parking Entry Remainings */}
			{/*Uncomment this when the Daryal park remainings needed*/}
				{/*
        <div className="bg-white p-2 sm:p-4 rounded-lg shadow">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="mb-2 sm:mb-0">
              <p className="text-xs sm:text-sm text-gray-500">{t('parkDaryalRemain')}</p>
              <p className={`text-lg sm:text-2xl font-bold ${
              stats.remaining.parking_entry_remain <= 0 ? 'text-red-600' : 
              stats.remaining.parking_entry_remain <= 5 ? 'text-yellow-600' : 
              'text-green-600'
            }`}>{stats.remaining.parking_entry_remain}</p>
            </div>
            <div className={`p-2 sm:p-3 rounded-full self-end sm:self-auto ${
              stats.remaining.parking_entry_remain <= 0 ? 'bg-red-100' : 
              stats.remaining.parking_entry_remain <= 5 ? 'bg-yellow-100' : 
              'bg-green-100'
            }`}>
              <Package className={`h-4 w-4 sm:h-6 sm:w-6 ${
              stats.remaining.parking_entry_remain <= 0 ? 'text-red-600' : 
              stats.remaining.parking_entry_remain <= 5 ? 'text-yellow-600' : 
              'text-green-600'
            }`} />
            </div>
          </div>
        </div>
				*/}
        {/* Parking Exit Remainings */}
        <div className="bg-white p-3 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">{t('parkDaryalRemain')}, {t('parkBagrGogremain')}</p>
              <p className={`text-xl font-bold ${
              stats.remaining.parking_exit_remain <= 0 ? 'text-red-600' :
              stats.remaining.parking_exit_remain <= 5 ? 'text-yellow-600' :
              'text-green-600'
            }`}>{stats.remaining.parking_exit_remain}</p>
            </div>
            <div className={`p-2 rounded-full ${
              stats.remaining.parking_exit_remain <= 0 ? 'bg-red-100' :
              stats.remaining.parking_exit_remain <= 5 ? 'bg-yellow-100' :
              'bg-green-100'
            }`}>
              <Package className={`h-4 w-4 ${
              stats.remaining.parking_exit_remain <= 0 ? 'text-red-600' :
              stats.remaining.parking_exit_remain <= 5 ? 'text-yellow-600' :
              'text-green-600'
            }`} />
            </div>
          </div>
        </div>

      </div>

      {/* RESPONSIVE FILTERS */}
      {isFilterExpanded && (
        <div className="bg-white p-3 sm:p-5 rounded-lg shadow mb-4 sm:mb-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <h3 className="text-base sm:text-lg font-medium text-gray-800 mb-3 flex items-center">
              <Filter className="h-4 w-4 sm:h-5 sm:w-5 mr-2 text-primary-600" />
              {t('searchFilters')}
            </h3>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('truckNumber')}</label>
                <input
                  type="text"
                  name="number"
                  value={filters.number}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('phone')}</label>
                <input
                  type="text"
                  name="phone"
                  value={filters.phone}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('dateFrom')}</label>
                <input
                  type="date"
                  name="dateFrom"
                  value={filters.dateFrom}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('dateTo')}</label>
                <input
                  type="date"
                  name="dateTo"
                  value={filters.dateTo}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('entryPoint')}</label>
                <select
                  name="entryPoint"
                  value={filters.entryPoint}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('all')}</option>
                  <option value="daryal">{t('daryal')}</option>
                  <option value="no_need">{t('no_need')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('exitPoint')}</label>
                <select
                  name="exitPoint"
                  value={filters.exitPoint}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('all')}</option>
                  <option value="bagratashen">{t('bagratashen')}</option>
                  <option value="gogavan">{t('gogavan')}</option>
                  <option value="no_need">{t('no_need')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('status')}</label>
                <select
                  name="status"
                  value={filters.status}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">{t('all')}</option>
                  <option value="on_road">{t('onRoad')}</option>
                  <option value="completed">{t('completed')}</option>
                  <option value="cancelled">{t('cancelled')}</option>
                </select>
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('transitIn')}</label>
                <input
                  type="number"
                  name="transit_in"
                  value={filters.transit_in}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
              
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1">{t('transitOut')}</label>
                <input
                  type="number"
                  name="transit_out"
                  value={filters.transit_out}
                  onChange={handleFilterChange}
                  className="w-full px-2 sm:px-3 py-1.5 sm:py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                />
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-2 pt-4">
              <button
                type="button"
                onClick={handleFilterReset}
                className="px-3 sm:px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-100 transition-colors text-sm"
              >
                {t('reset')}
              </button>
              
              <button
                type="submit"
                className="px-3 sm:px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors flex items-center justify-center text-sm"
              >
                <Search className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                {t('search')}
              </button>
            </div>
          </form>
        </div>
      )}
      
      {/* RESPONSIVE TABLE */}
      {(() => {
        const totalPages = Math.ceil(registrations.length / pageSize);
        const paged = registrations.slice((currentPage - 1) * pageSize, currentPage * pageSize);
        return (
      <div className="bg-white rounded-lg shadow w-full">
        <div className="p-2 sm:p-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
          <h3 className="text-sm font-medium text-gray-800">{t('registrationList')}</h3>
        </div>

        {/* Mobile Card View - Hidden on Desktop */}
        <div className="block lg:hidden overflow-y-auto max-h-[calc(100vh-550px)]">
          {isLoading ? (
            <div className="p-8 text-center">
              <div className="flex justify-center items-center">
                <svg className="animate-spin h-5 w-5 text-primary-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span className="text-sm">{t('loading')}</span>
              </div>
            </div>
          ) : registrations.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
              <p className="text-sm">{t('noRegistrationsFound')}</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {paged.map((registration) => (
                <div key={registration.id} className="p-4 hover:bg-gray-50">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium text-primary-600 text-sm">{registration.number}</p>
                      <p className="text-xs text-gray-500">{registration.company}</p>
                    </div>
                    <div className="flex space-x-1">
                      <button
                        onClick={() => handleEditRegistration(registration.id)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title={t('edit')}
                      >
                        <Edit className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(registration.id)}
                        className="text-red-600 hover:text-red-900 p-1"
                        title={t('delete')}
                      >
                        <Trash className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-gray-500">{t('status')}: </span>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${getStatusBadge(registration.status)}`}>
                        {t(registration.status)}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('departureDate')}: </span>
                      <span>{formatDate(registration.departure_date)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('transitInOut')}: </span>
                      <div className="flex gap-1">
                        <span className="px-1 py-0.5 text-xs rounded bg-blue-100 text-blue-800">
                          {t('IN')}: {registration.transit_in || 0}
                        </span>
                        <span className="px-1 py-0.5 text-xs rounded bg-red-100 text-red-800">
                          {t('OUT')}: {registration.transit_out || 0}
                        </span>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-500">{t('entryPoint')}: </span>
                      <span>{t(registration.entry_point || '-')}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* Desktop Table View - Hidden on Mobile */}
        <div className="hidden lg:block overflow-auto max-h-[calc(100vh-550px)]">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('truckNumber')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('trailerNumber')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('status')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('transitInOut')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('departureDate')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('entryPoint')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('terminalEntryDate')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('terminalExitDate')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('ticket')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('parking_40')}</th>
                <th className="px-3 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">{t('exitPoint')}</th>
                <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{t('actions')}</th>
              </tr>
            </thead>
            
            <tbody className="bg-white divide-y divide-gray-200">
              {isLoading ? (
                <tr>
                  <td colSpan="12" className="px-4 py-4 text-center">
                    <div className="flex justify-center items-center">
                      <svg className="animate-spin h-5 w-5 text-primary-600 mr-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      <span>{t('loading')}</span>
                    </div>
                  </td>
                </tr>
              ) : registrations.length === 0 ? (
                <tr>
                  <td colSpan="12" className="px-4 py-8 text-center text-gray-500">
                    <FileText className="h-12 w-12 mx-auto text-gray-400 mb-3" />
                    <p>{t('noRegistrationsFound')}</p>
                    <p className="text-sm text-gray-400 mt-1">{t('tryChangingFilters')}</p>
                  </td>
                </tr>
              ) : (
                paged.map((registration) => (
                  <tr key={registration.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 whitespace-nowrap text-primary-600 font-medium text-center">
                      {registration.number}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {registration.trailer_number || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-center">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs ${getStatusBadge(registration.status)}`}>
                        {t(registration.status)}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-center">
                      <div className="flex gap-1">
                        <span className="px-1.5 py-0.5 text-xs rounded bg-blue-100 text-blue-800">
                          {t('IN')}: {registration.transit_in || 0}
                        </span>
                        <span className="px-1.5 py-0.5 text-xs rounded bg-red-100 text-red-800">
                          {t('OUT')}: {registration.transit_out || 0}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {formatDate(registration.departure_date)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {t(registration.entry_point || '-')}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {formatDate(registration.terminal_entry_date)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {formatDate(registration.terminal_exit_date)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {registration.ticket || '-'}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {registration.parking_40 ?  (
                        <span className="text-green-500 font-bold">✓</span>) : (
                        <span className="text-red-500">-</span>)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-gray-500 text-center">
                      {t(registration.exit_point || '-')}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-right">
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => handleEditRegistration(registration.id)}
                          className={`p-1 ${registration.status === 'completed' ? 'text-gray-400 cursor-not-allowed' : 'text-blue-600 hover:text-blue-900'}`}
                          title={t('edit')}
                          disabled={registration.status === 'completed'}
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(registration.id)}
                          className={`p-1 ${registration.status === 'completed' ? 'text-gray-400 cursor-not-allowed' : 'text-red-600 hover:text-red-900'}`}
                          title={t('delete')}
                          disabled={registration.status === 'completed'}
                        >
                          <Trash className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!isLoading && registrations.length > pageSize && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500">{t('show')}:</span>
              {[20, 50, 100].map(n => (
                <button key={n} onClick={() => { setPageSize(n); setCurrentPage(1); }}
                  className={`px-2 py-1 text-xs rounded ${pageSize === n ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {n}
                </button>
              ))}
              <span className="text-xs text-gray-500 ml-2">
                {(currentPage - 1) * pageSize + 1}–{Math.min(currentPage * pageSize, registrations.length)} / {registrations.length}
              </span>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 1)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push('…');
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) => p === '…'
                  ? <span key={`e${idx}`} className="px-1 text-xs text-gray-400">…</span>
                  : <button key={p} onClick={() => setCurrentPage(p)}
                      className={`px-2 py-1 text-xs rounded border ${currentPage === p ? 'bg-primary-600 text-white border-primary-600' : 'border-gray-300 hover:bg-gray-50'}`}>
                      {p}
                    </button>
                )}
              <button onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}
                className="px-2 py-1 text-xs rounded border border-gray-300 disabled:opacity-40 hover:bg-gray-50">›</button>
            </div>
          </div>
        )}
      </div>
        );
      })()}

      {/* RESPONSIVE FOOTER */}
      {!isLoading && registrations.length > 0 && (
        <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-t border-gray-200 px-2 sm:px-4 py-3 sm:py-4 mt-0 rounded-b-lg shadow-sm">
          <div className="flex flex-col space-y-2 sm:space-y-3">
            {/* Header with total count */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-gray-200 pb-2">
              <div className="flex items-center space-x-2 mb-2 sm:mb-0">
                <div className="p-1.5 sm:p-2 bg-blue-100 rounded-full">
                  <ClipboardList className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
                </div>
                <span className="text-base sm:text-lg font-semibold text-gray-800">
                  {t('Total')}: <span className="text-blue-600">{stats.total}</span>
                </span>
              </div>
              <div className="text-xs text-gray-500 uppercase tracking-wide font-medium">
                {t('registrationList')}
              </div>
            </div>
            
            {/* Responsive Statistics grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-2 sm:gap-3">
              <div className="flex flex-col items-center p-1.5 sm:p-2 bg-white rounded-md shadow-sm border border-gray-200">
                <span className="text-xs text-gray-500 text-center mb-1">{t('transitIn_All')}</span>
                <span className="text-sm sm:text-lg font-bold text-blue-600">{stats.transit_in || 0}</span>
              </div>
              
              <div className="flex flex-col items-center p-1.5 sm:p-2 bg-white rounded-md shadow-sm border border-gray-200">
                <span className="text-xs text-gray-500 text-center mb-1">{t('transitOut_All')}</span>
                <span className="text-sm sm:text-lg font-bold text-red-600">{stats.transit_out || 0}</span>
              </div>
              
              <div className="flex flex-col items-center p-1.5 sm:p-2 bg-white rounded-md shadow-sm border border-gray-200">
                <span className="text-xs text-gray-500 text-center mb-1">{t('parking_40_All')}</span>
                <span className="text-sm sm:text-lg font-bold text-purple-600">{stats.parking_40 || 0}</span>
              </div>
              
              <div className="flex flex-col items-center p-1.5 sm:p-2 bg-white rounded-md shadow-sm border border-gray-200">
                <span className="text-xs text-gray-500 text-center mb-1">{t('parking_entry_All')}</span>
                <span className="text-sm sm:text-lg font-bold text-orange-600">{stats.parking_entry || 0}</span>
              </div>
              
              <div className="flex flex-col items-center p-1.5 sm:p-2 bg-white rounded-md shadow-sm border border-gray-200">
                <span className="text-xs text-gray-500 text-center mb-1">{t('parking_exit_All')}</span>
                <span className="text-sm sm:text-lg font-bold text-orange-600">{stats.parking_exit || 0}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      <RegistrationForm
        isOpen={isFormOpen}
        onClose={handleFormClose}
        onSubmit={handleRegistrationSubmit}
        initialData={selectedRegistration}
      />
    </div>
  );
};

export default RegistrationDashboard;